import newrelic from "@/lib/monitoring/newrelic";
import {
  API_ACCEPT,
  AuthApiPath,
  CONTENT_TYPE_FORM,
} from "@/lib/auth/constants";
import * as QueryString from "querystring";
import { validateEmail } from "@/lib/auth/validateRegistrationFields";
import { requestAndBindCsrfToHeaders } from "@/lib/auth/requestAndBindCsrfToHeaders";
import { buildApiEndpoint } from "@/lib/auth/buildApiEndpoint";

export interface ForgotPasswordParams {
  email: string; // 3 <= length <= 100, valid email format
}

export interface ForgotPasswordSuccessResponse {
  success: true;
  value: string; // HTML instructions, e.g. "We have e-mailed you instructions for setting your password..."
}

export interface ForgotPasswordErrorResponse {
  success: false;
  value: string; // Error message (rate limit, missing email, internal error, etc.)
}

export type ForgotPasswordResponse =
  | ForgotPasswordSuccessResponse
  | ForgotPasswordErrorResponse;

/**
 * Request a password reset email from the LMS.
 * @param params ForgotPasswordParams
 * @returns ForgotPasswordResponse
 *
 * The LMS always returns a JSON object with { success, value }.
 * Even if the email is not registered, success will be true and value will contain generic instructions.
 *
 * @throws Error if the network request fails (not for 4xx/5xx from LMS)
 */
export default async function forgotPassword({
  email,
}: ForgotPasswordParams): Promise<ForgotPasswordResponse> {
  const emailValidationErrorString = validateEmail(email);
  if (emailValidationErrorString) {
    return {
      success: false,
      value: emailValidationErrorString,
    } as ForgotPasswordErrorResponse;
  }

  const headers = await requestAndBindCsrfToHeaders({
    "Content-Type": CONTENT_TYPE_FORM,
    accept: API_ACCEPT,
  });
  const body = QueryString.stringify({ email });

  try {
    const res = await fetch(
      buildApiEndpoint(AuthApiPath.ReqForgotPasswordEmail),
      {
        method: "POST",
        headers: headers,
        body,
        credentials: "include",
      },
    );

    const contentType = res.headers.get("content-type");
    if (res.status === 200) {
      return { success: true, value: "" } as ForgotPasswordSuccessResponse;
    } else {
      // Log HTTP errors for non-200 responses
      const errorMessage = `Forgot password API returned ${res.status}`;

      newrelic.noticeError(new Error(errorMessage), {
        submodule: "auth",
        endpoint: "ReqForgotPasswordEmail",
        status: res.status,
        contentType: contentType || "unknown",
        errorType: "httpError",
        emailDomain: email ? email.split("@")[1] : "unknown",
        timestamp: new Date().toISOString(),
      });

      // This endpoint is weiiiird, it can return HTML -OR- JSON
      if (contentType?.includes("application/json")) {
        return (await res.json()) as ForgotPasswordResponse;
      } else if (contentType?.includes("text/html")) {
        const html = await res.text();
        return {
          success: false,
          value: html,
        } as ForgotPasswordErrorResponse;
      }
    }
  } catch (e) {
    newrelic.noticeError(e as Error, {
      submodule: "auth",
      endpoint: "ReqForgotPasswordEmail",
      context: "networkError",
      errorType: "networkError",
      emailDomain: email ? email.split("@")[1] : "unknown",
      timestamp: new Date().toISOString(),
    });

    return {
      success: false,
      value: (e as Error).toString(),
    } as ForgotPasswordErrorResponse;
  }
  return {
    success: false,
    value: "Unknown Error",
  } as ForgotPasswordErrorResponse;
}
